// Country-specific tax-identification metadata used to localise the Tax information form
// labels and placeholders. Driven by the publisher's country of tax residence so they see
// the local term for their tax ID (e.g., "Steuer-ID" for Germany) instead of generic
// IRS-style "Foreign TIN" phrasing.
//
// Keys are ISO 3166-1 alpha-2 country codes in LOWERCASE (matching the values used by
// the country `Select`). Anything not listed here falls back to the generic labels.

export type CountryTaxInfo = {
  // Short, locally recognised abbreviation used as the field label
  // (e.g., "Steuer-ID", "TFN", "PAN"). Falls back to the generic label when absent.
  individualLabel?: string;
  // Example value used as the input placeholder, formatted as a real-world ID would be.
  individualExample?: string;

  // Business equivalents (some countries reuse the personal ID for sole traders).
  businessLabel?: string;
  businessExample?: string;

  // VAT / GST equivalent. EU member states all have a country-prefixed VAT-ID;
  // non-EU countries that have a comparable indirect-tax registration also fit here.
  vatLabel?: string;
  vatExample?: string;
};

export const TAX_INFO_BY_COUNTRY: Record<string, CountryTaxInfo> = {
  us: {
    individualLabel: 'SSN or ITIN',
    individualExample: '123-45-6789',
    businessLabel: 'EIN',
    businessExample: '12-3456789',
  },
  gb: {
    individualLabel: 'NINO',
    individualExample: 'AB 12 34 56 C',
    businessLabel: 'UTR',
    businessExample: '1234567890',
    vatLabel: 'VAT registration number',
    vatExample: 'GB 123 4567 89',
  },
  de: {
    individualLabel: 'Steuer-ID',
    individualExample: '12 345 678 901',
    businessLabel: 'Steuernummer',
    businessExample: '123/456/78901',
    vatLabel: 'USt-IdNr.',
    vatExample: 'DE123456789',
  },
  fr: {
    individualLabel: 'Numéro fiscal',
    individualExample: '13 digits',
    businessLabel: 'SIREN',
    businessExample: '123 456 789',
    vatLabel: 'Numéro de TVA',
    vatExample: 'FR12 345678901',
  },
  it: {
    individualLabel: 'Codice Fiscale',
    individualExample: 'RSSMRA80A01H501Z',
    businessLabel: 'Partita IVA',
    businessExample: '12345678901',
    vatLabel: 'Partita IVA',
    vatExample: 'IT12345678901',
  },
  es: {
    individualLabel: 'NIF',
    individualExample: '12345678A',
    businessLabel: 'CIF',
    businessExample: 'A12345674',
    vatLabel: 'NIF-IVA',
    vatExample: 'ESX1234567X',
  },
  nl: {
    individualLabel: 'BSN',
    individualExample: '123456782',
    businessLabel: 'RSIN',
    businessExample: '123456789',
    vatLabel: 'BTW-nummer',
    vatExample: 'NL123456789B01',
  },
  be: {
    individualLabel: 'Rijksregisternummer',
    individualExample: '80.01.01-123.45',
    businessLabel: 'KBO/BCE',
    businessExample: '0123.456.789',
    vatLabel: 'BTW-nummer',
    vatExample: 'BE0123456789',
  },
  at: {
    individualLabel: 'Steueridentifikationsnummer',
    individualExample: '99 1234567',
    businessLabel: 'Steuernummer',
    businessExample: '12-345/6789',
    vatLabel: 'UID-Nummer',
    vatExample: 'ATU12345678',
  },
  pl: {
    individualLabel: 'PESEL',
    individualExample: '80010012345',
    businessLabel: 'NIP',
    businessExample: '123-45-67-890',
    vatLabel: 'NIP UE',
    vatExample: 'PL1234567890',
  },
  ie: {
    individualLabel: 'PPS number',
    individualExample: '1234567T',
    businessLabel: 'Tax Reference Number',
    businessExample: '1234567T',
    vatLabel: 'VAT number',
    vatExample: 'IE1234567T',
  },
  se: {
    individualLabel: 'Personnummer',
    individualExample: '800101-1234',
    businessLabel: 'Organisationsnummer',
    businessExample: '556677-8899',
    vatLabel: 'Momsregistreringsnummer',
    vatExample: 'SE556677889901',
  },
  no: {
    individualLabel: 'Fødselsnummer',
    individualExample: '01018012345',
    businessLabel: 'Organisasjonsnummer',
    businessExample: '987 654 321',
    vatLabel: 'Org.nr. MVA',
    vatExample: '987 654 321 MVA',
  },
  dk: {
    individualLabel: 'CPR-nummer',
    individualExample: '010180-1234',
    businessLabel: 'CVR-nummer',
    businessExample: '12345678',
    vatLabel: 'CVR / VAT',
    vatExample: 'DK12345678',
  },
  fi: {
    individualLabel: 'Henkilötunnus',
    individualExample: '010180-123A',
    businessLabel: 'Y-tunnus',
    businessExample: '1234567-8',
    vatLabel: 'ALV-numero',
    vatExample: 'FI12345678',
  },
  ch: {
    individualLabel: 'AHV-Nr.',
    individualExample: '756.1234.5678.90',
    businessLabel: 'UID',
    businessExample: 'CHE-123.456.789',
    vatLabel: 'MWST-Nr.',
    vatExample: 'CHE-123.456.789 MWST',
  },
  pt: {
    individualLabel: 'NIF',
    individualExample: '123456789',
    businessLabel: 'NIPC',
    businessExample: '500123456',
    vatLabel: 'NIF (IVA)',
    vatExample: 'PT123456789',
  },
  cz: {
    individualLabel: 'Rodné číslo',
    individualExample: '800101/1234',
    businessLabel: 'IČO',
    businessExample: '12345678',
    vatLabel: 'DIČ',
    vatExample: 'CZ12345678',
  },
  ro: {
    individualLabel: 'CNP',
    individualExample: '1800101123456',
    businessLabel: 'CUI',
    businessExample: 'RO12345678',
    vatLabel: 'Cod TVA',
    vatExample: 'RO12345678',
  },
  hu: {
    individualLabel: 'Adóazonosító jel',
    individualExample: '8123456789',
    businessLabel: 'Adószám',
    businessExample: '12345678-1-12',
    vatLabel: 'Közösségi adószám',
    vatExample: 'HU12345678',
  },
  gr: {
    individualLabel: 'ΑΦΜ',
    individualExample: '123456789',
    businessLabel: 'ΑΦΜ',
    businessExample: '123456789',
    vatLabel: 'ΑΦΜ (VAT)',
    vatExample: 'EL123456789',
  },
  au: {
    individualLabel: 'TFN',
    individualExample: '123 456 789',
    businessLabel: 'ABN',
    businessExample: '12 345 678 901',
    vatLabel: 'GST registration',
    vatExample: '12 345 678 901',
  },
  nz: {
    individualLabel: 'IRD number',
    individualExample: '12-345-678',
    businessLabel: 'IRD number',
    businessExample: '12-345-678',
    vatLabel: 'GST number',
    vatExample: '123-456-789',
  },
  ca: {
    individualLabel: 'SIN',
    individualExample: '123-456-789',
    businessLabel: 'BN',
    businessExample: '123456789RT0001',
    vatLabel: 'GST/HST number',
    vatExample: '123456789RT0001',
  },
  in: {
    individualLabel: 'PAN',
    individualExample: 'ABCDE1234F',
    businessLabel: 'PAN',
    businessExample: 'AABCC1234F',
    vatLabel: 'GSTIN',
    vatExample: '22AAAAA0000A1Z5',
  },
  br: {
    individualLabel: 'CPF',
    individualExample: '123.456.789-09',
    businessLabel: 'CNPJ',
    businessExample: '12.345.678/0001-95',
  },
  mx: {
    individualLabel: 'RFC',
    individualExample: 'XEXX010101000',
    businessLabel: 'RFC',
    businessExample: 'XAXX010101000',
  },
  ar: {
    individualLabel: 'CUIT/CUIL',
    individualExample: '20-12345678-9',
    businessLabel: 'CUIT',
    businessExample: '30-12345678-9',
  },
  jp: {
    individualLabel: 'My Number',
    individualExample: '1234 5678 9012',
    businessLabel: 'Corporate Number',
    businessExample: '1234567890123',
  },
  sg: {
    individualLabel: 'NRIC',
    individualExample: 'S1234567A',
    businessLabel: 'UEN',
    businessExample: '201912345A',
    vatLabel: 'GST registration',
    vatExample: '200012345M',
  },
  za: {
    individualLabel: 'Tax reference number',
    individualExample: '1234567890',
    businessLabel: 'Tax reference number',
    businessExample: '1234567890',
    vatLabel: 'VAT registration',
    vatExample: '4123456789',
  },
  ae: {
    individualLabel: 'Emirates ID',
    individualExample: '784-1980-1234567-1',
    businessLabel: 'Trade licence number',
    businessExample: 'CN-1234567',
    vatLabel: 'TRN',
    vatExample: '100123456700003',
  },
  ua: {
    individualLabel: 'ІНН',
    individualExample: '1234567890',
    businessLabel: 'ЄДРПОУ',
    businessExample: '12345678',
  },
};

export const getCountryTaxInfo = (countryCode: string | undefined): CountryTaxInfo | undefined => {
  if (!countryCode) return undefined;
  return TAX_INFO_BY_COUNTRY[countryCode.toLowerCase()];
};
